/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect} from 'react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useLocation} from 'react-router-dom'

// Components
import {Box} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'

// Project Components
import Seo from '../../components/seo'
import CMSContent from '../../components/cms-content'
import RecommendedProducts from '../../components/recommended-products'

// Others
import {useInitialDataLayer} from '../../tracking/hooks/use-initial-data-layer'

// Hooks
import useEinstein from '../../hooks/use-einstein'
import useMultiSite from '../../hooks/use-multi-site'

// Constants
import {HOME_CONTENT_ASSET_ID} from '../../constants'
import useEffectEvent from '../../hooks/use-effect-event'
import {HeaderContentHomePage} from '../../components/header-content'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useOcapiFolder} from '../../hooks/useOcapi'
import {Helmet} from 'react-helmet'
import {useHomePageStructuredData} from '../../hooks/use-structured-data'
import useConvertPageTags from '../../convert-api/hooks/use-convert-page-tags'
import {
    ProductQuickviewModal,
    useProductQuickviewModal
} from '../../components/product-quickview-modal'
import LenisProvider from '../../contexts/lenis'
import CmsModules from '../../leap/modules'
import {Chakra3Theme} from '../../leap/theme'
import ModuleContainer from '../../leap/components/module-container'
import {HeaderVariant} from '../../leap/components/navigation/header/header-variant'
import {getHeaderBgFromFirstModule} from '../../leap/modules/get-header-bg-from-first-module'
import {useContentAsset} from '../../hooks/useScapiCustomApi'
import {useChakraContext} from '@chakra-ui/react3'

/**
 * This is the home page for Retail React App.
 * The page is created for demonstration purposes.
 * The page renders SEO metadata and a few promotion
 * categories and products, data is from local file.
 */
const Home = () => {
    const einstein = useEinstein()
    const {pathname} = useLocation()
    const {site} = useMultiSite()
    const system = useChakraContext()
    const productQuickviewModalProps = useProductQuickviewModal()
    productQuickviewModalProps.onRecommendedProductClick = productQuickviewModalProps.onOpen

    useInitialDataLayer({pageType: 'Homepage'})
    useConvertPageTags({pageType: 'homepage'})

    const {res} = useServerContext()
    const {homePageOrganizationJSONLDScript, homePageWebSiteJSONLDScript} =
        useHomePageStructuredData()

    if (res) {
        const cacheConfig = getConfig().app.cache
        if (cacheConfig?.home) {
            res.set('Cache-Control', cacheConfig.home)
        }
    }

    const {data: rootFolder} = useOcapiFolder({parameters: {fdid: 'root'}})

    const {data: modules} = useContentAsset(
        {parameters: {assetId: HOME_CONTENT_ASSET_ID}},
        {enabled: Boolean(rootFolder?.c_isLeapContent)}
    )

    const headerBg = rootFolder?.c_isLeapContent
        ? getHeaderBgFromFirstModule(system, rootFolder.c_colorTheme, modules)
        : undefined

    /**************** Einstein ****************/
    const onMount = useEffectEvent(() => einstein.sendViewPage(pathname))
    useEffect(() => {
        onMount()
    }, [onMount])

    return (
        <>
            <HeaderVariant
                variant="floating"
                bg={
                    rootFolder?.c_isLeapContent
                        ? headerBg?.bg ?? 'background.section.subtle'
                        : undefined
                }
                theme={headerBg?.theme}
            />
            <HeaderContentHomePage />
            <Box
                data-testid="home-page"
                sx={!rootFolder?.c_isLeapContent ? {px: {base: 0, lg: 6, xl: 8}} : null}
            >
                <Seo
                    title={rootFolder?.pageTitle}
                    description={rootFolder?.pageDescription}
                    keywords={rootFolder?.pageKeywords}
                />
                <Helmet>
                    {homePageOrganizationJSONLDScript}
                    {homePageWebSiteJSONLDScript}
                </Helmet>
                {rootFolder?.c_isLeapContent ? (
                    <Chakra3Theme
                        theme={rootFolder.c_colorTheme ? rootFolder.c_colorTheme : 'light'}
                    >
                        <LenisProvider>
                            <CmsModules assetId={HOME_CONTENT_ASSET_ID} modules={modules} />
                        </LenisProvider>
                    </Chakra3Theme>
                ) : (
                    <CMSContent id={HOME_CONTENT_ASSET_ID} showError={false} />
                )}
            </Box>
            {/* Product Recommendations */}
            <ModuleContainer background="subtle" spacing="recommender">
                <RecommendedProducts
                    title={
                        <FormattedMessage
                            defaultMessage="Other families also like"
                            id="recommended_products.title.other_families_also_like"
                        />
                    }
                    recommender={`HP_recom_${site.id}`}
                    onRecommendedProductClick={productQuickviewModalProps.onRecommendedProductClick}
                />
            </ModuleContainer>
            <ProductQuickviewModal {...productQuickviewModalProps} />
        </>
    )
}

Home.getTemplateName = () => 'home'

export default Home
